import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { ProjectState, formatStageState } from '../../../dtos/project-state';
import {
  ScreeningAgreementResult,
  ScreeningAgreementPair,
  agreementInterpretationLabel,
  agreementReasonLabel,
  agreementThresholdStatusLabel,
} from '../../../dtos/screening-agreement';
import { ScreeningAgreementService } from '../../../services/screening-agreement.service';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-agreement-stats-card',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './agreement-stats-card.component.html',
})
export class AgreementStatsCardComponent implements OnChanges {
  @Input() projectId = '';
  @Input() stage!: ProjectState;
  @Input() projectState: ProjectState | string | null = null;
  @Input() visible = false;
  @Input() compact = false;

  readonly ProjectState = ProjectState;
  loading = false;
  errorMessage = '';
  agreement: ScreeningAgreementResult | null = null;
  detailsOpen = false;

  constructor(
    private agreementService: ScreeningAgreementService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['stage'] || changes['projectState'] || changes['visible']) {
      this.loadIfReady();
    }
  }

  retry(): void {
    this.loadIfReady(true);
  }

  get isOfficial(): boolean {
    return this.agreement?.calculationType === 'OFFICIAL';
  }

  get finalizedAt(): string {
    const agreement = this.agreement;
    if (!agreement || agreement.calculationType !== 'OFFICIAL') {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(agreement.finalizedAt));
  }

  stageLabel(): string {
    return formatStageState(this.stage);
  }

  interpretationLabel(value = this.agreement?.interpretation): string {
    return agreementInterpretationLabel(value);
  }

  thresholdStatusLabel(value = this.agreement?.thresholdStatus): string {
    return agreementThresholdStatusLabel(value);
  }

  reasonLabel(): string {
    return agreementReasonLabel(this.agreement?.nonComputableReason);
  }

  formatKappa(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not computable';
    }
    return value.toFixed(2);
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not available';
    }
    return `${value.toFixed(1)}%`;
  }

  formatThreshold(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not configured';
    }
    return value.toFixed(2);
  }

  reviewerPairLabel(pair: ScreeningAgreementPair): string {
    const first = pair.firstReviewerName || `User #${pair.firstReviewerId}`;
    const second = pair.secondReviewerName || `User #${pair.secondReviewerId}`;
    return `${first} / ${second}`;
  }

  trackPair(_: number, pair: ScreeningAgreementPair): string {
    return `${pair.firstReviewerId}-${pair.secondReviewerId}`;
  }

  get finalizedByUserName(): string {
    const agreement = this.agreement;
    if (!agreement || agreement.calculationType !== 'OFFICIAL') {
      return '';
    }
    return agreement.finalizedByUserName || `User #${agreement.finalizedByUserId}`;
  }

  private loadIfReady(force = false): void {
    if (!this.visible || !this.projectId || !this.stage) {
      this.agreement = null;
      this.errorMessage = '';
      this.loading = false;
      this.markForCheck();
      return;
    }

    if (this.loading && !force) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.agreement = null;
    this.markForCheck();

    if (this.shouldRequestPreviewFirst()) {
      this.loadPreview();
    } else {
      this.loadOfficialWithActiveStageFallback();
    }
  }

  private loadPreview(): void {
    this.agreementService
      .getPreview(this.projectId, this.stage)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          this.agreement = result;
          this.markForCheck();
        },
        error: (error) => this.handleError(error),
      });
  }

  private loadOfficialWithActiveStageFallback(): void {
    this.agreementService.getOfficial(this.projectId, this.stage).subscribe({
      next: (result) => {
        this.agreement = result;
        this.loading = false;
        this.markForCheck();
      },
      error: (error) => {
        if (this.isNotFound(error) && this.isCurrentStage()) {
          this.loadPreview();
          return;
        }
        this.loading = false;
        this.handleError(error);
        this.markForCheck();
      },
    });
  }

  private shouldRequestPreviewFirst(): boolean {
    return (
      this.stage === ProjectState.TITLE_ABSTRACT_SCREENING &&
      this.projectState === ProjectState.TITLE_ABSTRACT_SCREENING
    );
  }

  private isCurrentStage(): boolean {
    return this.projectState === this.stage;
  }

  private isNotFound(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      this.errorMessage = 'You do not have access to reviewer agreement statistics.';
      this.markForCheck();
      return;
    }
    if (error instanceof HttpErrorResponse && error.status === 404) {
      this.errorMessage = 'Official reviewer agreement is not available yet.';
      this.markForCheck();
      return;
    }
    this.errorMessage = 'Could not load reviewer agreement statistics.';
    this.markForCheck();
  }

  private markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }
}
